import { Icon } from '@iconify/react'
import { Empty, Radio } from 'antd'

import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { Spinner } from 'common/components/Spinner'

import type { StripeTerminal } from 'features/Procedure/state/procedure-api.type'

import CardIcon from '../assets/card.svg?react'

import styles from './select-terminal-view.module.scss'

type Props = {
  amount: number
  maxAmount: number
  onAmountChange: (next: number) => void
  terminals: StripeTerminal[]
  isLoading: boolean
  isError: boolean
  selectedTerminalId: number | null
  onSelectTerminal: (terminalId: number) => void
  onCancel: () => void
  onSend: () => void
  isSending: boolean
}

const sanitizeDigits = (raw: string): string => raw.replace(/\D/g, '')

const digitsToAmount = (digits: string, maxAmount: number): number => {
  const cents = digits === '' ? 0 : Number.parseInt(digits, 10)
  if (!Number.isFinite(cents) || cents < 0) return 0
  const dollars = cents / 100
  return dollars > maxAmount ? maxAmount : dollars
}

const formatAmount = (value: number): string => {
  if (!Number.isFinite(value) || value < 0) return '0.00'
  return value.toFixed(2)
}

export const SelectTerminalView = ({
  amount,
  maxAmount,
  onAmountChange,
  terminals,
  isLoading,
  isError,
  selectedTerminalId,
  onSelectTerminal,
  onCancel,
  onSend,
  isSending,
}: Props) => {
  const isTerminalOnline = (terminal: StripeTerminal): boolean => terminal.status !== 'offline'

  const onlineCount = terminals.filter(isTerminalOnline).length

  const isCashBilledFlow = amount === 0

  const handleAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = sanitizeDigits(event.target.value)
    onAmountChange(digitsToAmount(digits, maxAmount))
  }

  const handleAmountInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select()
  }

  const amountInputDisplay = formatAmount(amount)

  const primaryButtonLabel = isCashBilledFlow ? 'Mark as Billed' : 'Send payment request'
  const primaryButtonDisabled = isSending || (!isCashBilledFlow && selectedTerminalId === null)

  const renderTerminalList = () => {
    if (isLoading) {
      return (
        <div className={styles.emptyState}>
          <Spinner isLoading />
        </div>
      )
    }
    if (isError) {
      return <div className={styles.errorState}>Failed to load terminals.</div>
    }
    if (terminals.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Empty description='No terminals available' />
        </div>
      )
    }

    return (
      <div className={styles.terminalList}>
        {terminals.map((terminal) => {
          const isOnline = isTerminalOnline(terminal)
          const isInteractive = isOnline && !isCashBilledFlow
          const isSelected = selectedTerminalId === terminal.id && !isCashBilledFlow
          const rowClass = [
            styles.terminalRow,
            isSelected ? styles.terminalRowSelected : '',
            !isInteractive ? styles.terminalRowDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')

          const handleClick = () => {
            if (!isInteractive) return
            onSelectTerminal(terminal.id)
          }

          return (
            <div
              key={terminal.id}
              className={rowClass}
              role='button'
              tabIndex={isInteractive ? 0 : -1}
              aria-disabled={!isInteractive}
              onClick={handleClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleClick()
                }
              }}>
              <Radio
                checked={isSelected}
                disabled={!isInteractive}
                onChange={() => undefined}
                className={styles.radioControl}
              />
              <span className={styles.brand}>
                <Icon icon='bi:stripe' width={32} height={32} />
              </span>
              <span className={styles.terminalLabel}>{terminal.name}</span>
              <span
                className={[styles.statusBadge, !isOnline ? styles.statusBadgeOffline : '']
                  .filter(Boolean)
                  .join(' ')}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.terminalsHeader}>
        <span>Select terminal</span>
        <span className={styles.onlineCount}>
          <span className={styles.onlineDot} />
          {onlineCount} online
        </span>
      </div>

      {renderTerminalList()}

      <div className={styles.amountCard}>
        <div className={styles.amountTextBlock}>
          <span className={styles.amountLabel}>Payment amount</span>
          <span className={styles.amountValue}>
            <span className={styles.amountSymbol}>$</span>
            <input
              type='text'
              inputMode='decimal'
              className={styles.amountInput}
              value={amountInputDisplay}
              size={Math.max(1, amountInputDisplay.length)}
              onChange={handleAmountInputChange}
              onFocus={handleAmountInputFocus}
              aria-label='Payment amount'
              disabled={isSending}
            />
          </span>
        </div>
        <div className={styles.cardIcon}>
          <CardIcon width={48} height={48} />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          proportion={BUTTON_PROPORTION.LARGE}
          onClick={onSend}
          disabled={primaryButtonDisabled}
          loading={isSending}
          className={styles.sendButton}>
          {primaryButtonLabel}
        </Button>
        <Button
          modifier={BUTTON_MODIFIER.SECONDARY}
          proportion={BUTTON_PROPORTION.LARGE}
          onClick={onCancel}
          disabled={isSending}
          className={styles.cancelButton}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
